import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ['form', 'pageInput', 'submitBtn']
  static values = {
    autoTrigger: Boolean,
    pageLimit: Number,
    excludeFromUrl: Array,
    listenFacetChange: Boolean
  }

  connect() {
    this.page = 1

    if (this.listenFacetChangeEnabled()) {
      this.setupFacetChangeListener()
    }

    if (this.autoTriggerValue) {
      this.submit()
    }

    document.querySelectorAll('.filter-search').forEach((input) => { input.disabled = false })

    if (this.hasFormTarget) {
      this.formTarget.addEventListener('submit', (event) => {
        event.preventDefault()
        this.submit()
      })
    }
  }

  disconnect() {
    document.removeEventListener('change', this._facetChange, true)
  }

  listenFacetChangeEnabled() {
    return this.listenFacetChangeValue || this.hasListenFacetChangeValue
  }

  setupFacetChangeListener() {
    this._facetChange = (e) => {
      const t = e.target
      if (!t || t.type !== 'checkbox') return
      if (!t.name || !t.name.startsWith('filters[')) return

      let aggsName = null
      const container = t.closest('.filter-list-content')
      if (container && container.id) {
        aggsName = container.id.replace(/-filter-list$/, '')
      }

      if (aggsName) {
        this.updateHiddenFields(aggsName)
      }

      if (this.hasPageInputTarget) this.pageInputTarget.value = 1
      this.submit()
    }
    document.addEventListener('change', this._facetChange, true)
  }

  updateHiddenFields(aggsName) {
    const list = document.getElementById(`${aggsName}-filter-list`)
    const selector = `input[name="filters[conditions][${aggsName}][value][]"]`
    const cbs = Array.from((list || document).querySelectorAll(selector))

    const hiddenFields = document.getElementById(`${aggsName}-hidden-fields`)
    const selected = new Set(
      hiddenFields
        ? Array.from(hiddenFields.querySelectorAll('input.selected-hidden-value')).map(el => el.value)
        : []
    )

    // Add visible checked, remove only visible unchecked
    cbs.forEach(cb => { if (cb.checked) selected.add(cb.value) })
    cbs.forEach(cb => { if (!cb.checked) selected.delete(cb.value) })

    const anySelected = selected.size > 0
    const keyEl = document.getElementById(`${aggsName}_hidden_key`)
    const compEl = document.getElementById(`${aggsName}_hidden_comparator`)
    if (keyEl) keyEl.disabled = !anySelected
    if (compEl) compEl.disabled = !anySelected

    if (!hiddenFields) return
    hiddenFields.querySelectorAll('input.selected-hidden-value').forEach(el => el.remove())
    selected.forEach(val => {
      const h = document.createElement('input')
      h.type = 'hidden'
      h.name = `filters[conditions][${aggsName}][value][]`
      h.value = val
      h.className = 'selected-hidden-value'
      hiddenFields.appendChild(h)
    })
  }

  eventSubmit(event) {
    if (event) { event.preventDefault() }

    // For search forms, always navigate to fresh page on Enter key
    // This ensures before_action runs and chat resets for new queries
    const isSearchForm = this.formTarget.id === 'unified-search-form'
    if (isSearchForm) {
      const formData = new FormData(this.formTarget)
      const query = formData.get('query')
      if (query && query.trim()) {
        window.location.href = `/search?query=${encodeURIComponent(query.trim())}`
        return
      }
    }

    this.submit()
  }

  submit() {
    const redirected = this.handleBeforeSubmit()
    if (redirected) return

    const action = this.formTarget.action
    const method = (this.formTarget.method || 'POST').toUpperCase()

    this.dispatch('before-submit')

    if (method === 'GET') {
      $.ajax({ url: action, type: 'GET' })
      return
    }

    const data = new FormData(this.formTarget)
    $.ajax({
      url: action,
      type: 'POST',
      data: data,
      processData: false,
      contentType: false
    })
  }

  reload() {
    this.pageInputTarget.value = 1
    this.submit()
    this.dispatch('reload')
  }

  nextPage(event) {
    let pageNumber = event.detail.page
    if (!Number.isInteger(pageNumber)) { pageNumber = this.page + 1 }
    this.page = pageNumber
    this.pageInputTarget.value = pageNumber
    this.submit()
    if (this.page > this.pageLimitValue) {
      this.dispatch('page-limit')
    }
  }

  formAttrs() {
    let data = new FormData(this.formTarget)
    data.delete("authenticity_token")
    data.delete("page")
    if (this.hasExcludeFromUrlValue) {
      this.excludeFromUrlValue.forEach(key => {
        if (this.excludeFromUrlValue.includes(key)) {
          data.delete(key)
        }
      })
    }

    // Create URLSearchParams from deduplicated entries
    const urlParams = new URLSearchParams()
    for (const [key, value] of this.deduplicateFormData(data)) {
      urlParams.append(key, value)
    }

    return urlParams.toString()
  }

  deduplicateFormData(formData) {
    const uniqueEntries = new Map()

    // Collect all entries and deduplicate by key-value pair
    for (const [key, value] of formData.entries()) {
      const entryKey = `${key}=${value}`
      if (!uniqueEntries.has(entryKey)) {
        uniqueEntries.set(entryKey, [key, value])
      }
    }

    // Return array of deduplicated [key, value] pairs
    return Array.from(uniqueEntries.values())
  }

  handleBeforeSubmit() {
    let path = location.pathname
    const qs = this.formAttrs()
    const base = this.formTarget.action.split('?')[0]

    if (!path.includes('/inventory')) {
      this.formTarget.action = `${base}?${qs}`
    }

    if (path === '/') {
      window.location.href = `/search?${qs}`
      return
    }
    history.pushState({}, '', `${path}?${qs}`)
    this.dispatch('fetching-new-results', { detail: { page: this.page } })
  }

  resetPage() {
    this.pageInputTarget.value = 1
  }
}
